// GAVION inbound calls (owner request 2026-10-21): the « Appel entrant » page (#/appel-entrant?tel=…&evt=answer|end),
// opened by the rep's softphone (tools/microsip/gavion-incoming.cmd). SQL: public.start_inbound_call /
// public.end_inbound_call (supabase/schemas/80_gavion_inbound_calls.sql); FakeRest twin: fakeDialerApi.
import { normalizePhone } from "../phone";
import type { DialOutcome } from "./types";

export type InboundEvent = "answer" | "end";

/** The number as the softphone sent it, reduced to digits (and a leading +), at most 20 characters. */
export function sanitizeInboundNumber(raw: string | null | undefined): string {
  const s = String(raw ?? "").trim();
  const digits = s.replace(/\D/g, "").slice(0, 20);
  return s.startsWith("+") && digits ? `+${digits}` : digits;
}

export type InboundParams = {
  /** What the softphone sent, sanitised (shown when it is not a valid number). */
  raw: string;
  /** E.164 (SQL public.normalize_phone's rule), or null: hidden, foreign or malformed. */
  phone: string | null;
  event: InboundEvent;
};

/** `?tel=…&evt=answer|end` (any other evt = answer). */
export function parseInboundParams(search: string): InboundParams {
  const q = new URLSearchParams(search);
  const raw = sanitizeInboundNumber(q.get("tel"));
  return {
    raw,
    phone: normalizePhone(raw),
    event: q.get("evt") === "end" ? "end" : "answer",
  };
}

/** One lead or contact whose number is the caller's (SQL private.inbound_phone_matches). */
export type InboundMatch = {
  company_id: number;
  company_name: string;
  city: string | null;
  contact_id: number | null;
  contact_name: string | null;
};

/**
 * The results of the outcome dialog after an inbound call: a conversation took place, so no « Pas de réponse » /
 * « Message laissé » (and R25, the « Appel manqué » email, can never follow), no « Mauvais numéro » (the caller's
 * number is not the lead's line) and no « Passer ».
 */
export const INBOUND_OUTCOMES: readonly DialOutcome[] = [
  "talked_callback",
  "demo_booked",
  "brochure_sent",
  "not_interested",
  "do_not_call",
];

/** How long a stashed « Appel entrant » link waits for the sign-in (then it is dropped). */
export const INBOUND_STASH_TTL_MS = 10 * 60_000;
export const INBOUND_STASH_KEY = "gavion.inbound-call";
export const INBOUND_ROUTE = "/appel-entrant";

/** The hash route to stash, or null when the URL is not an « Appel entrant » link. */
export function inboundRouteOf(hash: string): string | null {
  const route = hash.startsWith("#") ? hash.slice(1) : hash;
  return route === INBOUND_ROUTE || route.startsWith(`${INBOUND_ROUTE}?`)
    ? route
    : null;
}

type Stash = { route: string; at: number };

/** Remembers the link before the sign-in (a Microsoft sign-in returns to the home page without it). */
export function stashInboundRoute(
  storage: Pick<Storage, "setItem">,
  hash: string,
  nowMs: number,
): void {
  const route = inboundRouteOf(hash);
  if (!route) return;
  try {
    storage.setItem(
      INBOUND_STASH_KEY,
      JSON.stringify({ route, at: nowMs } satisfies Stash),
    );
  } catch {
    /* storage full or blocked: the normal login return still works */
  }
}

/** The stashed link if it is still fresh (always removed). */
export function takeInboundRoute(
  storage: Pick<Storage, "getItem" | "removeItem">,
  nowMs: number,
): string | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(INBOUND_STASH_KEY);
    storage.removeItem(INBOUND_STASH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<Stash>;
    if (
      typeof s.route !== "string" ||
      typeof s.at !== "number" ||
      nowMs - s.at > INBOUND_STASH_TTL_MS ||
      s.at > nowMs + 60_000
    )
      return null;
    return inboundRouteOf(s.route);
  } catch {
    return null;
  }
}

type MatchCompany = {
  id: unknown;
  name?: string | null;
  city?: string | null;
  phone_e164?: string | null;
  phone_number?: string | null;
};
type MatchContact = {
  id: unknown;
  company_id?: unknown;
  first_name?: string | null;
  last_name?: string | null;
  phone_jsonb?: unknown;
};

/** « First Last », or null when both are empty (SQL: nullif(btrim(concat_ws(' ', first, last)), '')). */
export const inboundContactName = (k: {
  first_name?: string | null;
  last_name?: string | null;
}): string | null =>
  [k.first_name, k.last_name]
    .filter((x) => x != null && x !== "")
    .join(" ")
    .trim() || null;

const byName = (a: InboundMatch, b: InboundMatch) =>
  (a.company_name < b.company_name
    ? -1
    : a.company_name > b.company_name
      ? 1
      : 0) ||
  a.company_id - b.company_id ||
  (a.contact_id ?? -1) - (b.contact_id ?? -1);

/**
 * TS twin of private.inbound_phone_matches (FakeRest demo): the leads whose main number is `phone` and the contacts
 * that have it (each number normalised), with the contact's lead; at most 20, by lead name.
 */
export function inboundMatches(
  phone: string,
  companies: readonly MatchCompany[],
  contacts: readonly MatchContact[],
): InboundMatch[] {
  const byId = new Map(companies.map((c) => [Number(c.id), c]));
  const out: InboundMatch[] = [];
  for (const c of companies)
    if ((c.phone_e164 ?? normalizePhone(c.phone_number)) === phone)
      out.push({
        company_id: Number(c.id),
        company_name: c.name ?? "",
        city: c.city ?? null,
        contact_id: null,
        contact_name: null,
      });
  for (const k of contacts) {
    const c = k.company_id == null ? undefined : byId.get(Number(k.company_id));
    if (!c || !Array.isArray(k.phone_jsonb)) continue;
    const has = (k.phone_jsonb as unknown[]).some(
      (e) =>
        !!e &&
        typeof e === "object" &&
        normalizePhone((e as { number?: unknown }).number) === phone,
    );
    if (has)
      out.push({
        company_id: Number(c.id),
        company_name: c.name ?? "",
        city: c.city ?? null,
        contact_id: Number(k.id),
        contact_name: inboundContactName(k),
      });
  }
  return out.sort(byName).slice(0, 20);
}
